import type { ParseKeys } from 'i18next'

import { ApiError } from '@/lib/api'

/* The RFC 9457 problem type suffix is the CONTRACT (AD-22); the message is not.
 *
 * A problem document carries `title` and `detail` written server-side in
 * English, for a developer reading a log. Rendering those to a user is how a
 * Russian interface acquires English sentences in exactly the moments things go
 * wrong — which is when a reader is least able to cope with a second language
 * (AD-27).
 *
 * So the client maps the stable suffix to its own translated message. The
 * suffix set below mirrors the server's checked-in registry in
 * platform/httpx/problem.go, and a test asserts the two agree: a suffix the
 * server can send with no entry here would fall through to a generic message
 * and lose the specific one somebody wrote.
 */

/** Every suffix the server's registry declares. */
export const PROBLEM_SUFFIXES = [
  'validation-failed',
  'auth-required',
  'forbidden',
  'not-found',
  'conflict',
  'idempotency-conflict',
  'rate-limited',
  'payload-too-large',
  'unsupported-media-type',
  'unavailable',
  'internal',
] as const

export type ProblemSuffix = (typeof PROBLEM_SUFFIXES)[number]

/** A translated title and detail for one problem. */
export interface ProblemMessage {
  titleKey: ParseKeys
  detailKey: ParseKeys
  /** Whether offering "try again" is honest. A 403 will answer 403 again. */
  retryable: boolean
}

const MESSAGES: Record<ProblemSuffix, ProblemMessage> = {
  'validation-failed': {
    titleKey: 'errors.problems.validationFailed.title',
    detailKey: 'errors.problems.validationFailed.detail',
    retryable: false,
  },
  'auth-required': {
    titleKey: 'errors.problems.authRequired.title',
    detailKey: 'errors.problems.authRequired.detail',
    retryable: false,
  },
  forbidden: {
    titleKey: 'errors.problems.forbidden.title',
    detailKey: 'errors.problems.forbidden.detail',
    retryable: false,
  },
  'not-found': {
    titleKey: 'errors.problems.notFound.title',
    detailKey: 'errors.problems.notFound.detail',
    retryable: false,
  },
  conflict: {
    titleKey: 'errors.problems.conflict.title',
    detailKey: 'errors.problems.conflict.detail',
    retryable: false,
  },
  'idempotency-conflict': {
    titleKey: 'errors.problems.idempotencyConflict.title',
    detailKey: 'errors.problems.idempotencyConflict.detail',
    retryable: false,
  },
  'rate-limited': {
    titleKey: 'errors.problems.rateLimited.title',
    detailKey: 'errors.problems.rateLimited.detail',
    // The only refusal that becomes allowed by waiting.
    retryable: true,
  },
  'payload-too-large': {
    titleKey: 'errors.problems.payloadTooLarge.title',
    detailKey: 'errors.problems.payloadTooLarge.detail',
    retryable: false,
  },
  'unsupported-media-type': {
    titleKey: 'errors.problems.unsupportedMediaType.title',
    detailKey: 'errors.problems.unsupportedMediaType.detail',
    retryable: false,
  },
  unavailable: {
    titleKey: 'errors.problems.unavailable.title',
    detailKey: 'errors.problems.unavailable.detail',
    // Not retryable: the capability is absent from this installation, so the
    // same request answers the same way until somebody configures the service.
    // Offering "try again" would be a button that cannot work.
    retryable: false,
  },
  internal: {
    titleKey: 'errors.problems.internal.title',
    detailKey: 'errors.problems.internal.detail',
    retryable: true,
  },
}

/** No connection, DNS failure, a proxy that never answered. */
const NETWORK: ProblemMessage = {
  titleKey: 'errors.network.title',
  detailKey: 'errors.network.detail',
  retryable: true,
}

/** A response that is not a problem document — a gateway's own error page, or a
 *  status the registry does not cover. */
const UNKNOWN: ProblemMessage = {
  titleKey: 'errors.problems.unknown.title',
  detailKey: 'errors.problems.unknown.detail',
  retryable: true,
}

function isKnownSuffix(value: string): value is ProblemSuffix {
  return (PROBLEM_SUFFIXES as readonly string[]).includes(value)
}

/**
 * Maps any thrown value to a translated message.
 *
 * Takes `unknown` rather than `ApiError` because that is what a catch block and
 * a TanStack Query `error` actually hold: a caller forced to narrow first would
 * narrow inconsistently, and the network case — which is not an ApiError at all
 * — is the one that matters most on a phone in the field.
 */
export function describeProblem(error: unknown): ProblemMessage {
  if (!(error instanceof ApiError)) {
    return NETWORK
  }

  if (isKnownSuffix(error.code)) {
    return MESSAGES[error.code]
  }

  // A 5xx that arrived as a gateway's HTML error page rather than a problem
  // document still deserves the server-fault message, not "unknown".
  if (error.status >= 500) {
    return MESSAGES.internal
  }

  return UNKNOWN
}

/**
 * Field-level validation failures, keyed by field path.
 *
 * Returns an empty object for anything that is not a validation problem, so a
 * form can spread it unconditionally rather than branching.
 */
export function fieldErrors(error: unknown): Record<string, string[]> {
  if (!(error instanceof ApiError)) return {}
  return error.problem.errors ?? {}
}

/**
 * The translation key for one field-level error code.
 *
 * The server sends a stable token — `required`, `invalid`, `too-long` — for the
 * same reason it sends a suffix: the message belongs in the language the reader
 * is using. An unrecognised code falls back to a generic message rather than
 * rendering the raw token, which would read as a bug to a user.
 */
export function fieldErrorKey(code: string): ParseKeys {
  switch (code) {
    case 'required':
      return 'common.form.required'
    case 'invalid':
      return 'common.form.invalid'
    case 'too-long':
      return 'common.form.tooLong'
    case 'malformed':
      return 'common.form.malformed'
    default:
      return 'common.form.invalid'
  }
}
